import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { Link as RouterLink } from 'react-router-dom';
import { Icon } from '@iconify/react';

import cloudApi from '../../../app/api/cloudApi';
import TextInputComponent from '../../../components/input/TextInputComponent';
import TimetablesPanel from './components/TimetablesPanel';
import TimetableEditorDialog from './components/TimetableEditorDialog';
import GateAssignmentGrid from './components/GateAssignmentGrid';
import AccessLevelsList from './components/AccessLevelsList';
import { createDefaultTimetables } from './utils';

export default function AccessLevelBuilder() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const account = useSelector((state) => state.app.account);
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [error, setError] = useState(null);

  // Gates — loaded the same way as the Face Scanner Devices & Gates page:
  // gate groups (each containing its gates) via the cloud FaceScannerGate API,
  // scoped by the currently selected account (Redux), not site.accountId.
  const [gateGroups, setGateGroups] = useState([]);
  const [loadingGates, setLoadingGates] = useState(false);

  const gates = useMemo(
    () =>
      gateGroups.flatMap((group) =>
        (group.gates || group.Gates || []).map((gate) => ({
          ...gate,
          id: gate.id ?? gate.Id,
          name: gate.name ?? gate.Name,
          groupName: group.name ?? group.Name,
        }))
      ),
    [gateGroups]
  );

  // Timetables — reusable named weekly schedules. Seeded with 4 always-available
  // presets so the common cases need zero setup before assigning to a gate.
  const [timetables, setTimetables] = useState(createDefaultTimetables());
  const [timetableDialogOpen, setTimetableDialogOpen] = useState(false);
  const [editingTimetable, setEditingTimetable] = useState(null);
  // When the "Create custom timetable…" option is picked directly from a gate
  // card, remember which gate should be auto-assigned once the dialog is saved.
  const [pendingAssignGateId, setPendingAssignGateId] = useState(null);

  // Access level builder — name + a gateId -> timetableId assignment map.
  // Visual, one click per gate: no separate "add pairing row" step.
  const [accessLevelName, setAccessLevelName] = useState('');
  const [assignments, setAssignments] = useState({});
  const [savedAccessLevels, setSavedAccessLevels] = useState([]);

  // Load site data
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user || !siteId) {
      setLoading(false);
      return;
    }

    const loadSiteData = async () => {
      try {
        const response = await cloudApi.getSite(auth.user.access_token, siteId);
        setSite(response.data);
      } catch (err) {
        console.error('Error loading site:', err);
        setError('Failed to load site data');
        enqueueSnackbar('Failed to load site data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadSiteData();
  }, [auth.isAuthenticated, auth.user, siteId, enqueueSnackbar]);

  const fetchGateGroups = async () => {
    if (!auth.user?.access_token || !account?.id || !siteId) return;

    setLoadingGates(true);
    try {
      const response = await cloudApi.getFaceScannerGateGroups(auth.user.access_token, siteId, account.id);
      if (response.data && Array.isArray(response.data)) {
        setGateGroups(response.data);
      }
    } catch (err) {
      console.error('Error fetching gate groups:', err);
      enqueueSnackbar('Failed to fetch gate groups', { variant: 'warning' });
    } finally {
      setLoadingGates(false);
    }
  };

  // Fetch gate groups once the site (and selected account) are ready.
  useEffect(() => {
    if (!site || !auth.user?.access_token || !account?.id) return;
    fetchGateGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, account?.id]);

  const handleRefreshGates = () => fetchGateGroups();

  // Timetable dialog handlers
  const handleNewTimetable = () => {
    setPendingAssignGateId(null);
    setEditingTimetable(null);
    setTimetableDialogOpen(true);
  };

  const handleEditTimetable = (timetable) => {
    setPendingAssignGateId(null);
    setEditingTimetable(timetable);
    setTimetableDialogOpen(true);
  };

  /** Triggered from a gate card's "Create custom timetable…" option. */
  const handleRequestNewTimetableForGate = (gateId) => {
    setPendingAssignGateId(gateId ?? null);
    setEditingTimetable(null);
    setTimetableDialogOpen(true);
  };

  const handleDeleteTimetable = (id) => {
    const inUse = Object.values(assignments).includes(id);
    if (inUse) {
      enqueueSnackbar('Unassign this timetable from its gate(s) before deleting it', { variant: 'warning' });
      return;
    }
    setTimetables((prev) => prev.filter((tt) => tt.id !== id));
    enqueueSnackbar('Timetable removed', { variant: 'info' });
  };

  const handleSaveTimetable = (timetable) => {
    setTimetables((prev) => {
      const exists = prev.some((tt) => tt.id === timetable.id);
      return exists ? prev.map((tt) => (tt.id === timetable.id ? timetable : tt)) : [...prev, timetable];
    });
    setTimetableDialogOpen(false);
    enqueueSnackbar(`Timetable "${timetable.name}" saved`, { variant: 'success' });

    if (pendingAssignGateId) {
      setAssignments((prev) => ({ ...prev, [pendingAssignGateId]: timetable.id }));
      setPendingAssignGateId(null);
    }
  };

  // Assign (or clear) a timetable directly on a gate card — no extra clicks.
  const handleAssignGate = (gateId, timetableId) => {
    setAssignments((prev) => {
      if (!timetableId) {
        const next = { ...prev };
        delete next[gateId];
        return next;
      }
      return { ...prev, [gateId]: timetableId };
    });
  };

  // Access level builder handlers
  const handleSaveAccessLevel = () => {
    if (!accessLevelName.trim()) {
      enqueueSnackbar('Please enter a name for the access level', { variant: 'warning' });
      return;
    }

    const completeCombos = Object.entries(assignments)
      .filter(([, timetableId]) => timetableId)
      .map(([gateKey, timetableId]) => ({ id: uuidv4(), gateKey, timetableId }));

    if (completeCombos.length === 0) {
      enqueueSnackbar('Assign at least one gate to a timetable', { variant: 'warning' });
      return;
    }

    const accessLevel = {
      id: uuidv4(),
      name: accessLevelName.trim(),
      combos: completeCombos,
      createdAt: new Date().toISOString(),
    };

    setSavedAccessLevels((prev) => [...prev, accessLevel]);
    enqueueSnackbar('Access level saved', { variant: 'success' });

    setAccessLevelName('');
    setAssignments({});
  };

  const handleLoadSaved = (al) => {
    setAccessLevelName(al.name || '');
    setAssignments(
      al.combos.reduce((acc, c) => {
        acc[c.gateKey] = c.timetableId;
        return acc;
      }, {})
    );
    enqueueSnackbar(`Loaded "${al.name}" into builder`, { variant: 'info' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteSaved = (id) => {
    setSavedAccessLevels((prev) => prev.filter((al) => al.id !== id));
    enqueueSnackbar('Access level removed', { variant: 'info' });
  };

  const handleCancel = () => {
    navigate('/sites');
  };

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/sites')}>
          Return to Sites
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{site ? `${site.name} - Access Level Builder | CloudLoq` : 'Access Level Builder | CloudLoq'}</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link component={RouterLink} to="/sites" underline="hover" color="inherit">
              Sites
            </Link>
            <Typography color="text.primary">Access Level Builder</Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Icon icon="mdi:table-network" width="32" height="32" />
            <Typography variant="h4">Access Level Builder {site && `- ${site.name}`}</Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleRefreshGates}
            disabled={loadingGates}
            startIcon={loadingGates ? <CircularProgress size={20} /> : <Icon icon="mdi:refresh" />}
          >
            Refresh Gates
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Pick a <strong>Timetable</strong> directly on each gate card below — 4 common presets are ready to go, or
            create your own. Different gates can use different timetables; saving bundles them into one Access
            Level.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Left Column — Timetables + Builder */}
          <Grid xs={12} lg={8}>
            <TimetablesPanel
              timetables={timetables}
              onNew={handleNewTimetable}
              onEdit={handleEditTimetable}
              onDelete={handleDeleteTimetable}
            />

            <Card>
              <CardHeader
                title="Build Access Level"
                subheader="Name this access level, then click a timetable on each gate you want to include"
              />
              <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3, maxWidth: 480 }}>
                  <TextInputComponent
                    value={accessLevelName}
                    setValue={setAccessLevelName}
                    label="Access Level Name"
                    placeholder="e.g., Day Shift, Contractors"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle2">Gates</Typography>
                  {loadingGates && <CircularProgress size={14} />}
                </Box>

                <GateAssignmentGrid
                  gates={gates}
                  timetables={timetables}
                  assignments={assignments}
                  onAssign={handleAssignGate}
                  onRequestNewTimetable={handleRequestNewTimetableForGate}
                />
              </Box>

              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleSaveAccessLevel} startIcon={<Icon icon="mdi:content-save" />}>
                  Save Access Level
                </Button>
              </Box>
            </Card>
          </Grid>

          {/* Right Column — Saved Access Levels */}
          <Grid xs={12} lg={4}>
            <AccessLevelsList
              accessLevels={savedAccessLevels}
              gates={gates}
              timetables={timetables}
              onLoad={handleLoadSaved}
              onDelete={handleDeleteSaved}
            />
          </Grid>
        </Grid>
      </Container>

      <TimetableEditorDialog
        open={timetableDialogOpen}
        timetable={editingTimetable}
        onClose={() => {
          setTimetableDialogOpen(false);
          setPendingAssignGateId(null);
        }}
        onSave={handleSaveTimetable}
      />
    </>
  );
}
